import React, { useEffect, useState } from 'react';
import OrderItem from './OrderItem';

const Orders = ({ menuSections }) => {
	const [orders, setOrders] = useState(() => {
		const savedOrders = localStorage.getItem('orders');
		return savedOrders ? JSON.parse(savedOrders, (key, value) => {
			if (key === 'orderedAt') return new Date(value);
			return value;
		}) : [];
	});

	const [newOrderItems, setNewOrderItems] = useState([]);
	const [selectedItem, setSelectedItem] = useState('');
	const [selectedQuantity, setSelectedQuantity] = useState(1);
	const [nextOrderId, setNextOrderId] = useState(() => {
		const savedId = localStorage.getItem('nextOrderId');
		return savedId ? parseInt(savedId, 10) : 1;
	});

	// Save to localStorage whenever orders change
	useEffect(() => {
		localStorage.setItem('orders', JSON.stringify(orders));
	}, [orders]);

	// Save nextOrderId to localStorage when it changes
	useEffect(() => {
		localStorage.setItem('nextOrderId', nextOrderId.toString());
	}, [nextOrderId]);

	const handleCreateOrder = () => {
		if (newOrderItems.length === 0) return;

		const newOrder = {
			id: nextOrderId,
			orderedAt: new Date(),
			items: newOrderItems,
			total: newOrderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(2)
		};

		setOrders(prev => [...prev, newOrder]);
		setNextOrderId(prev => prev + 1);
		setNewOrderItems([]);
		setSelectedItem('');
		setSelectedQuantity(1);
	};

	// Add item to new order
	const handleAddItem = () => {
		const [sectionIndex, itemIndex] = selectedItem.split('-').map(Number);
		const item = menuSections[sectionIndex]?.items[itemIndex];

		if (item) {
			const existingItem = newOrderItems.find(i => i.name === item.name);
			const quantity = Number(selectedQuantity);

			const updatedItems = existingItem
				? newOrderItems.map(i =>
					i.name === item.name
						? { ...i, quantity: i.quantity + quantity }
						: i
				)
				: [...newOrderItems, {
					...item,
					quantity,
					price: parseFloat(item.price.replace('$', ''))
				}];

			setNewOrderItems(updatedItems);
		}
	};

	// delete the order
	const [hiddenOrders, setHiddenOrders] = useState(new Set());
	const handleHideOrder = (order) => {
		setHiddenOrders((prev) => new Set(prev).add(order.id));
	};

	return (
		<div>
			<h1 className="text-3xl font-bold mb-6 text-gold-dark">Orders</h1>
			{/* Order Creation Form */}
			<div className="bg-white p-5 rounded-lg shadow-md mb-6">
				<h2 className="text-xl font-bold mb-4">Create New Order</h2>
				<div className="flex gap-3 mb-4 flex-wrap">
					<select
						className="flex-1 p-2 border rounded min-w-[200px]"
						value={selectedItem}
						onChange={(e) => setSelectedItem(e.target.value)}
					>
						<option value="">Select Menu Item</option>
						{menuSections.map((section, sectionIndex) => (
							<optgroup key={sectionIndex} label={section.title}>
								{section.items.map((item, itemIndex) => (
									<option
										key={`${sectionIndex}-${itemIndex}`}
										value={`${sectionIndex}-${itemIndex}`}
									>
										{item.name} - {item.price}
									</option>
								))}
							</optgroup>
						))}
					</select>

					<input
						type="number"
						min="1"
						value={selectedQuantity}
						onChange={(e) => setSelectedQuantity(e.target.value)}
						className="w-20 p-2 border rounded"
					/>

					<button
						onClick={handleAddItem}
						className="px-4 py-2 bg-gold-dark text-white rounded hover:bg-gold"
					>
						Add Item
					</button>
				</div>

				{newOrderItems.length > 0 && (
					<div className="mb-4">
						<h3 className="font-bold mb-2">Order Items:</h3>
						{newOrderItems.map((item, index) => (
							<div key={index} className="flex justify-between mb-1">
								<span>{item.quantity}x {item.name}</span>
								<span>${(item.price * item.quantity).toFixed(2)}</span>
							</div>
						))}
						<div className="flex justify-between font-bold mt-2">
							<span>Total:</span>
							<span>${newOrderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(2)}</span>
						</div>
					</div>
				)}

				<button
					onClick={handleCreateOrder}
					className="w-full py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-300"
					disabled={newOrderItems.length === 0}
				>
					Create Order (#{nextOrderId})
				</button>
			</div>

			{/* Orders List */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
				{orders
				.filter((order) => !hiddenOrders.has(order.id))
				.map(order => (
					<OrderItem 
					key={order.id}
					order={order}
					orders={orders}
					setOrders={setOrders}
					menuSections={menuSections}
					onDelete={() => handleHideOrder(order)}
					/>
				))}
			</div>
		</div>
	);
};

export default Orders;